import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, Search, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, AlertCircle, CheckCircle2 } from './icons';
import { cleanConditionSyntax } from '../utils/templateParser';
import { scanTextForSyntaxErrors } from '../utils/syntaxValidator.js';
import './FinalOutputPreview.css';
export function FinalOutputPreview({ editor, variables, pageSettings, onClose }) {
    const [previewElements, setPreviewElements] = useState([]);
    const [syntaxErrorCount, setSyntaxErrorCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [matches, setMatches] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [previewZoom, setPreviewZoom] = useState(100);
    const previewZoomAnchorRef = useRef(null);
    const previewMousePosRef = useRef({ x: 0, y: 0, isOver: false });
    const previewScrollContainerRef = useRef(null);

    const previewDocRef = useRef(null);
    const searchInputRef = useRef(null);
    useEffect(() => {
        if (!editor)
            return;
        const generatePreview = () => {
            let docErrorCount = 0;
            const getSimilarity = (s1, s2) => {
                if (s1 === s2) return 1.0;
                if (!s1 || !s2) return 0.0;
                const set1 = new Set();
                for (let i = 0; i < s1.length - 1; i++) set1.add(s1.substring(i, i + 2));
                let matches = 0;
                for (let i = 0; i < s2.length - 1; i++) {
                    if (set1.has(s2.substring(i, i + 2))) matches++;
                }
                return (2.0 * matches) / (s1.length - 1 + s2.length - 1);
            };
            const getNodeTableFingerprint = (tableNode) => {
                const rows = (tableNode.content || []).filter((r) => r.type === 'tableRow');
                return rows.map(r => {
                    const cells = r.content || [];
                    return cells.map(cell => {
                        let text = '';
                        const extract = (n) => {
                            if (n.type === 'text') text += n.text;
                            if (n.attrs?.variableKey || n.attrs?.label) text += ' ' + (n.attrs.variableKey || n.attrs.label);
                            if (n.content) n.content.forEach(extract);
                        };
                        extract(cell);
                        return text.replace(/Â«|Â»|<<|>>|\[|\]|\{|\}/g, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
                    }).join(':::');
                }).filter(r => r.length > 0).join('###');
            };
            const seenTableFingerprints = [];

            const syntaxVioletStyle = {
                color: '#6d28d9',
                backgroundColor: '#ede9fe',
                padding: '0 2px',
                borderRadius: '2px',
                fontWeight: 'bold',
            };

            function renderConditionContent(condStr) {
                if (!condStr) return null;
                let clean = cleanConditionSyntax(condStr);
                clean = clean.replace(/^\[\s*/, '').replace(/\s*\]$/, '').trim();
                if (!clean) return null;
                const condRegex = /(<<[^>]+>>|\*\*|__|&&|\|\||===|!==|==|!=|>=|<=|\bEXISTS\b|\bnot exists\b|\bcontains\b|\bnot contains\b|[{}\[\]|@$])/gi;
                const parts = clean.split(condRegex);
                return (
                    <>
                        {parts.map((part, idx) => {
                            if (!part) return null;
                            if (/^<<[^>]+>>$/.test(part)) {
                                return (
                                    <span key={idx} style={{
                                        backgroundColor: '#ffff00',
                                        color: '#000000',
                                        padding: '0 2px',
                                        display: 'inline',
                                        fontWeight: 'bold'
                                    }}>
                                        {part}
                                    </span>
                                );
                            }
                            if (/^(\*\*|__|&&|\|\||===|!==|==|!=|>=|<=|\bEXISTS\b|\bnot exists\b|\bcontains\b|\bnot contains\b|[{}\[\]|@$])$/i.test(part)) {
                                return (
                                    <span key={idx} style={syntaxVioletStyle}>
                                        {part}
                                    </span>
                                );
                            }
                            return <React.Fragment key={idx}>{part}</React.Fragment>;
                        })}
                    </>
                );
            }

            const renderSignatureBox = (node, key, customStyle = {}) => {
                const w = node.attrs?.width || '1.0';
                const h = node.attrs?.height || '1.0';
                let filename = (node.attrs?.filename || 'signature.png').trim();
                if (!filename) filename = 'signature.png';

                const align = node.attrs?.align || 'left';
                let condStr = cleanConditionSyntax(node.attrs?.condition, node.attrs?.conditionData);
                if (condStr) {
                    condStr = condStr.replace(/^\[\s*/, '').replace(/\s*\]$/, '').trim();
                }

                const alignPrefix = align === 'right' ? '=> ' : align === 'center' ? '== ' : '';

                const containerStyle = {
                    textAlign: align || 'left',
                    width: '100%',
                    margin: '6px 0',
                    lineHeight: 1.5,
                    ...customStyle,
                };

                if (condStr) {
                    const cleanAlign = (alignPrefix || '').trim();
                    return (
                        <div key={key} style={containerStyle}>
                            <span style={syntaxVioletStyle}>{'{'}</span>
                            {cleanAlign ? <span style={syntaxVioletStyle}>{cleanAlign + ' '}</span> : <span style={syntaxVioletStyle}>{' '}</span>}
                            <span style={syntaxVioletStyle}>{`! ${w} ${h} ${filename}`}</span>
                            {' '}
                            <span style={syntaxVioletStyle}>{'['}</span>
                            {renderConditionContent(condStr)}
                            <span style={syntaxVioletStyle}>{']'}</span>
                            {' '}
                            <span style={syntaxVioletStyle}>{'}'}</span>
                        </div>
                    );
                }

                return (
                    <div key={key} style={containerStyle}>
                        {alignPrefix && align !== 'left' ? <span style={{ ...syntaxVioletStyle, marginRight: '4px' }}>{alignPrefix}</span> : null}
                        <span style={syntaxVioletStyle}>{`! ${w} ${h} ${filename}`}</span>
                    </div>
                );
            };

            const isTableDuplicateInPreview = (fp) => {
                if (!fp) return false;
                for (const existing of seenTableFingerprints) {
                    if (existing === fp || getSimilarity(existing, fp) >= 0.78) return true;
                }
                return false;
            };

            function decodeHtmlEntities(str) {
                if (!str) return '';
                return String(str)
                    .replace(/&amp;/g, '&')
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;|&apos;/g, "'")
                    .replace(/&nbsp;/g, ' ')
                    .replace(/\u00a0/g, ' ');
            }

            const editorJson = JSON.parse(JSON.stringify(editor.getJSON()));
            let elements = [];
            let globalIndex = 0;
            function getVariableLabel(node) {
                const rawKey = node.attrs?.variableKey;
                const rawLabel = node.attrs?.label;
                const cleanKey = decodeHtmlEntities(rawKey);
                const cleanLabel = decodeHtmlEntities(rawLabel);

                const v = variables.find(item =>
                    item.key === rawKey ||
                    item.key === cleanKey ||
                    item.label === rawLabel ||
                    item.label === cleanLabel ||
                    decodeHtmlEntities(item.key) === cleanKey ||
                    decodeHtmlEntities(item.label) === cleanLabel
                );
                const result = v ? v.label : (cleanLabel || cleanKey || 'Unknown');
                return decodeHtmlEntities(result);
            }
            function buildConditionString(attrs) {
                if (!attrs)
                    return '';
                const rulesList = attrs.rules || attrs.conditions || [];
                if (Array.isArray(rulesList) && rulesList.length > 0) {
                    const parts = rulesList.map((r) => {
                        const fieldKey = decodeHtmlEntities((r.field || r.variable || '')).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>?/gi, '').replace(/<\/?b[^>]*>?/gi, '').replace(/\*\*/g, '').trim();
                        const vObj = variables.find(item => item.key === fieldKey || item.label === fieldKey || decodeHtmlEntities(item.key) === fieldKey || decodeHtmlEntities(item.label) === fieldKey);
                        const vLabel = decodeHtmlEntities(vObj ? vObj.label : fieldKey).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>?/gi, '').replace(/<\/?b[^>]*>?/gi, '').replace(/\*\*/g, '').trim();
                        const op = decodeHtmlEntities((r.operator || 'exists')).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>?/gi, '').replace(/<\/?b[^>]*>?/gi, '').trim();
                        const val = decodeHtmlEntities((r.value !== undefined && r.value !== null ? String(r.value) : '')).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>?/gi, '').replace(/<\/?b[^>]*>?/gi, '').replace(/\*\*/g, '').trim();
                        if (op === 'exists' || op === 'is_not_empty') {
                            return `<<${vLabel}>>`;
                        }
                        else if (op === 'not_exists' || op === 'is_empty') {
                            return `<<${vLabel}>> == ''`;
                        }
                        else if (op === 'equals' || op === '==') {
                            return `<<${vLabel}>> == ${val}`;
                        }
                        else if (op === 'not_equals' || op === '!=') {
                            return `<<${vLabel}>> != ${val}`;
                        }
                        else if (op === 'greater_than' || op === '>') {
                            return `<<${vLabel}>> > ${val}`;
                        }
                        else if (op === 'less_than' || op === '<') {
                            return `<<${vLabel}>> < ${val}`;
                        }
                        else if (op === 'greater_than_or_equal' || op === '>=') {
                            return `<<${vLabel}>> >= ${val}`;
                        }
                        else if (op === 'less_than_or_equal' || op === '<=') {
                            return `<<${vLabel}>> <= ${val}`;
                        }
                        else if (op === 'contains') {
                            return `<<${vLabel}>> contains ${val}`;
                        }
                        else if (op === 'does_not_contain') {
                            return `<<${vLabel}>> not contains ${val}`;
                        }
                        else if (op === 'OR' || op === '||') {
                            return `<<${vLabel}>>`;
                        }
                        return val ? `<<${vLabel}>> ${op} ${val}` : `<<${vLabel}>>`;
                    });
                    const joiner = (attrs.operator === 'OR' || attrs.operator === '||') ? ' || ' : ' && ';
                    return `[${parts.join(joiner)}]`;
                }
                else if (attrs.label) {
                    let l = decodeHtmlEntities(String(attrs.label)).replace(/<[^>]+>/g, '').replace(/<\/?strong[^>]*>?/gi, '').replace(/<\/?b[^>]*>?/gi, '').replace(/\*\*/g, '').trim();
                    l = l.replace(/\s+EXISTS\b/gi, '').replace(/\s+exists\b/gi, '').trim();
                    if (l.startsWith('[') && l.endsWith(']'))
                        return l;
                    return `[${l}]`;
                }
                return '';
            }

            function closeMark(itemList, startIdx, endIdx, marker) {
                if (startIdx < 0 || endIdx < 0 || startIdx >= itemList.length || endIdx >= itemList.length) return;

                if (!itemList[startIdx].text.startsWith(marker)) {
                    const m = itemList[startIdx].text.match(/^(\s*)([\s\S]*)$/);
                    if (m) itemList[startIdx].text = m[1] + marker + m[2];
                    else itemList[startIdx].text = marker + itemList[startIdx].text;
                }

                if (!itemList[endIdx].text.endsWith(marker)) {
                    const m = itemList[endIdx].text.match(/^([\s\S]*?)(\s*)$/);
                    if (m) itemList[endIdx].text = m[1] + marker + m[2];
                    else itemList[endIdx].text = itemList[endIdx].text + marker;
                }
            }

            function groupMark(itemList, isMarkProp, marker) {
                if (!itemList || itemList.length === 0) return itemList;

                const isWhitespace = (it) => !it.text || it.text.trim().length === 0;

                let inMark = false;
                let markStartIdx = -1;
                let lastMarkedContentIdx = -1;

                for (let i = 0; i < itemList.length; i++) {
                    const it = itemList[i];
                    const hasMark = Boolean(it[isMarkProp]);
                    const isWs = isWhitespace(it);

                    if (!inMark) {
                        if (hasMark && !isWs) {
                            inMark = true;
                            markStartIdx = i;
                            lastMarkedContentIdx = i;
                        }
                    } else {
                        if (hasMark && !isWs) {
                            lastMarkedContentIdx = i;
                        } else if (isWs) {
                            let nextHasMark = false;
                            for (let j = i + 1; j < itemList.length; j++) {
                                if (!isWhitespace(itemList[j])) {
                                    nextHasMark = Boolean(itemList[j][isMarkProp]);
                                    break;
                                }
                            }
                            if (!nextHasMark) {
                                closeMark(itemList, markStartIdx, lastMarkedContentIdx, marker);
                                inMark = false;
                                markStartIdx = -1;
                                lastMarkedContentIdx = -1;
                            }
                        } else if (!hasMark) {
                            closeMark(itemList, markStartIdx, lastMarkedContentIdx, marker);
                            inMark = false;
                            markStartIdx = -1;
                            lastMarkedContentIdx = -1;
                        }
                    }
                }

                if (inMark && markStartIdx !== -1 && lastMarkedContentIdx !== -1) {
                    closeMark(itemList, markStartIdx, lastMarkedContentIdx, marker);
                }

                return itemList;
            }

            function applyMarkupToNodes(contentList, isHeading = false) {
                if (!contentList || contentList.length === 0 || isHeading) return contentList;

                // Map nodes to items for grouping
                const items = contentList.map(node => {
                    const isBold = node.marks?.some(m => m.type === 'bold') ?? false;
                    const isUnderline = node.marks?.some(m => m.type === 'underline') ?? false;
                    const isClearSpace = node.marks?.some(m => m.type === 'clearSpaceBlock') ?? false;
                    const isDollar = node.marks?.some(m => m.type === 'dollarBlock') ?? false;
                    let text = '';
                    if (node.type === 'text') {
                        text = node.text || '';
                    } else if (node.type === 'variableTag') {
                        const label = getVariableLabel(node);
                        text = `<<${label}>>`;
                    }
                    return {
                        node,
                        text,
                        isBold,
                        isUnderline,
                        isClearSpace,
                        isDollar
                    };
                });

                // Group marks in nested order (innermost to outermost):
                // 1. bold (**)
                // 2. underline (__)
                // 3. clearSpace (@)
                // 4. dollar ($)
                let res = groupMark(items, 'isBold', '**');
                res = groupMark(res, 'isUnderline', '__');
                res = groupMark(res, 'isClearSpace', '@');
                res = groupMark(res, 'isDollar', '$');

                // Apply back formatted text
                return res.map(item => {
                    if (item.node.type === 'text') {
                        return {
                            ...item.node,
                            text: item.text
                        };
                    } else if (item.node.type === 'variableTag') {
                        const label = getVariableLabel(item.node);
                        const originalRaw = `<<${label}>>`;
                        let prefix = '';
                        let suffix = '';
                        if (item.text.startsWith(originalRaw)) {
                            suffix = item.text.substring(originalRaw.length);
                        } else if (item.text.endsWith(originalRaw)) {
                            prefix = item.text.substring(0, item.text.length - originalRaw.length);
                        } else {
                            const idx = item.text.indexOf(originalRaw);
                            if (idx !== -1) {
                                prefix = item.text.substring(0, idx);
                                suffix = item.text.substring(idx + originalRaw.length);
                            }
                        }
                        return {
                            ...item.node,
                            prefix,
                            suffix,
                            renderedText: item.text
                        };
                    }
                    return item.node;
                });
            }

            function renderInlineNode(node, parentStyle = {}, isHeading = false) {
                const key = globalIndex++;
                const style = { ...parentStyle, textDecoration: 'none' };
                let isBold = false;
                let isUnderline = false;
                if (node.marks) {
                    node.marks.forEach((mark) => {
                        if (mark.type === 'bold') {
                            style.fontWeight = 'bold';
                            isBold = true;
                        }
                        if (mark.type === 'underline') {
                            style.textDecoration = 'none';
                            isUnderline = true;
                        }
                        if (mark.type === 'italic')
                            style.fontStyle = 'italic';
                    });
                }
                const syntaxVioletStyle = {
                    color: '#6d28d9',
                    backgroundColor: '#ede9fe',
                    padding: '0 2px',
                    borderRadius: '2px',
                    fontWeight: 'bold',
                };

                function renderHighlightedText(text, textStyle, key, isBlockStart = false) {
                    if (!text) return null;

                    // Scan for syntax errors / stray delimiters
                    const errors = scanTextForSyntaxErrors(text, isBlockStart);
                    if (errors && errors.length > 0) {
                        docErrorCount += errors.length;
                        const elements = [];
                        let cur = 0;
                        errors.forEach((err, eIdx) => {
                            if (err.from > cur) {
                                const normalText = text.substring(cur, err.from);
                                elements.push(
                                    <React.Fragment key={`norm-${eIdx}`}>
                                        {renderNormalTokenizedText(normalText, textStyle)}
                                    </React.Fragment>
                                );
                            }
                            const errText = text.substring(err.from, err.to);
                            elements.push(
                                <span
                                    key={`err-${eIdx}`}
                                    className="syntax-error-highlight"
                                    title={err.message}
                                    style={{
                                        textDecoration: 'underline wavy #ef4444 1.5px',
                                        textUnderlineOffset: '3px',
                                        backgroundColor: 'rgba(239, 68, 68, 0.12)',
                                        color: '#b91c1c',
                                        borderRadius: '2px',
                                        padding: '0 2px',
                                        fontWeight: 'bold',
                                        cursor: 'help'
                                    }}
                                >
                                    {errText}
                                </span>
                            );
                            cur = err.to;
                        });
                        if (cur < text.length) {
                            const remainingText = text.substring(cur);
                            elements.push(
                                <React.Fragment key="norm-tail">
                                    {renderNormalTokenizedText(remainingText, textStyle)}
                                </React.Fragment>
                            );
                        }
                        return <span key={key} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', ...textStyle }}>{elements}</span>;
                    }

                    return renderNormalTokenizedText(text, textStyle, key);
                }

                function renderNormalTokenizedText(text, textStyle, key = undefined) {
                    if (!text) return null;
                    const tokenRegex = /(<page\s+break>|<line>|\*\*|__|###\s|##\s|#\s|=>\s|==\s|<=\s|&&|\|\||===|!==|==|!=|>=|<=|[{}\[\]|@$])/gi;
                    const parts = text.split(tokenRegex);
                    const wrapStyle = { wordBreak: 'break-word', overflowWrap: 'anywhere', ...textStyle };
                    if (parts.length === 1) {
                        return <span key={key} style={wrapStyle}>{text}</span>;
                    }
                    return (
                        <span key={key} style={wrapStyle}>
                            {parts.map((part, pIdx) => {
                                if (!part) return null;
                                const lower = part.toLowerCase();
                                if (lower === '<page break>' || lower === '<line>' || lower === '<line break>') {
                                    return (
                                        <span key={pIdx} style={{
                                            backgroundColor: '#ede9fe',
                                            color: '#6d28d9',
                                            padding: '0 2px',
                                            borderRadius: '2px',
                                            display: 'inline',
                                            fontWeight: 'normal',
                                        }} title={lower.includes('page') ? "Page Break" : lower.includes('break') ? "Line Break" : "Line"}>
                                            {part}
                                        </span>
                                    );
                                }
                                if (/^(\*\*|__|###\s|##\s|#\s|=>\s|==\s|<=\s|&&|\|\||===|!==|==|!=|>=|<=|[{}\[\]|@$])$/.test(part)) {
                                    return (
                                        <span key={pIdx} style={syntaxVioletStyle}>
                                            {part}
                                        </span>
                                    );
                                }
                                return <React.Fragment key={pIdx}>{part}</React.Fragment>;
                            })}
                        </span>
                    );
                }

                if (node.type === 'hardBreak' || node.type === 'br') {
                    return <br key={key} />;
                }
                if (node.type === 'text') {
                    let text = node.text || '';
                    if (node.marks?.some((m) => m.type === 'conditionalBlock')) {
                        text = text.replace(/^;\s*/, '');
                    }
                    return renderHighlightedText(text, style, key);
                }
                if (node.type === 'variableTag') {
                    const label = getVariableLabel(node);
                    const isNodeBold = isHeading ? false : (node.marks?.some(m => m.type === 'bold') || style.fontWeight === 'bold');
                    const prefix = isHeading ? '' : (node.prefix !== undefined ? node.prefix : (isNodeBold ? '**' : ''));
                    const suffix = isHeading ? '' : (node.suffix !== undefined ? node.suffix : (isNodeBold ? '**' : ''));
                    const affixStyle = {
                        color: '#6d28d9',
                        backgroundColor: '#ede9fe',
                        padding: '0 2px',
                        borderRadius: '2px',
                        fontWeight: 'bold',
                    };
                    return (<span key={key} style={{ display: 'inline' }}>
                        {prefix ? <span style={affixStyle}>{prefix}</span> : null}
                        <span style={{
                            ...style,
                            backgroundColor: '#ffff00',
                            color: '#000000',
                            padding: '0 2px',
                            display: 'inline',
                            fontWeight: isHeading ? (style.fontWeight || 'bold') : (isNodeBold ? 'bold' : 'normal'),
                        }} title={`Variable: ${label}`}>
                            {`<<${label}>>`}
                        </span>
                        {suffix ? <span style={affixStyle}>{suffix}</span> : null}
                    </span>);
                }
                if (node.type === 'image') {
                    return (<img key={key} src={node.attrs.src} alt={node.attrs.alt || ''} title={node.attrs.title || ''} style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '8px 0' }} />);
                }
                if (node.type === 'pageBreak') {
                    return (
                        <span key={key} style={{
                            ...style,
                            backgroundColor: '#ede9fe',
                            color: '#6d28d9',
                            padding: '0 2px',
                            borderRadius: '2px',
                            display: 'inline',
                            fontWeight: 'normal',
                        }} title="Page Break">
                            &lt;page break&gt;
                        </span>
                    );
                }
                if (node.type === 'lineBreakBlock') {
                    return (<span key={key} style={{
                        ...style,
                        backgroundColor: '#ede9fe',
                        color: '#6d28d9',
                        padding: '0 2px',
                        borderRadius: '2px',
                        display: 'inline',
                        fontWeight: 'normal',
                    }} title="Line Break">
                        &lt;line break&gt;
                    </span>);
                }
                if (node.type === 'horizontalLineBlock' || node.type === 'horizontalRule') {
                    return (
                        <span key={key} style={{
                            ...style,
                            backgroundColor: '#ede9fe',
                            color: '#6d28d9',
                            padding: '0 2px',
                            borderRadius: '2px',
                            display: 'inline',
                            fontWeight: 'normal',
                        }} title="Line">
                            &lt;line&gt;
                        </span>
                    );
                }
                if (node.type === 'whiteBlankImage') {
                    return renderSignatureBox(node, key, { display: 'inline-block' });
                }
                return null;
            }
            function renderInlineGrouped(rawContentList, parentStyle = {}, isHeading = false) {
                const syntaxVioletStyle = {
                    color: '#6d28d9',
                    backgroundColor: '#ede9fe',
                    padding: '0 2px',
                    borderRadius: '2px',
                    fontWeight: 'bold',
                };
                function renderConditionContent(condStr) {
                    if (!condStr) return null;
                    let clean = condStr.replace(/^\[\s*/, '').replace(/\s*\]$/, '');
                    const condRegex = /(<<[^>]+>>|\*\*|__|&&|\|\||===|!==|==|!=|>=|<=|\bEXISTS\b|\bnot exists\b|\bcontains\b|\bnot contains\b|[{}\[\]|@$])/gi;
                    const parts = clean.split(condRegex);
                    return (
                        <>
                            {parts.map((part, idx) => {
                                if (!part) return null;
                                if (/^<<[^>]+>>$/.test(part)) {
                                    return (
                                        <span key={idx} style={{
                                            backgroundColor: '#ffff00',
                                            color: '#000000',
                                            padding: '0 2px',
                                            display: 'inline',
                                            fontWeight: 'bold'
                                        }}>
                                            {part}
                                        </span>
                                    );
                                }
                                if (/^(\*\*|__|&&|\|\||===|!==|==|!=|>=|<=|\bEXISTS\b|\bnot exists\b|\bcontains\b|\bnot contains\b|[{}\[\]|@$])$/i.test(part)) {
                                    return (
                                        <span key={idx} style={syntaxVioletStyle}>
                                            {part}
                                        </span>
                                    );
                                }
                                return <React.Fragment key={idx}>{part}</React.Fragment>;
                            })}
                        </>
                    );
                }
                const contentList = applyMarkupToNodes(rawContentList, isHeading);
                if (!contentList || contentList.length === 0)
                    return [];
                const result = [];
                let i = 0;
                while (i < contentList.length) {
                    const child = contentList[i];
                    const marks = child.marks || [];
                    const condMark = marks.find((m) => m.type === 'conditionalBlock');
                    const optMark = marks.find((m) => m.type === 'optionalBlock');
                    const clearMark = marks.find((m) => m.type === 'clearSpaceBlock');
                    const dollarMark = marks.find((m) => m.type === 'dollarBlock');
                    if (condMark) {
                        const condRuleId = condMark.attrs?.ruleId || JSON.stringify(condMark.attrs?.rules || condMark.attrs?.conditions || '');
                        const group = [];
                        while (i < contentList.length) {
                            const curChild = contentList[i];
                            const curCond = curChild.marks?.find((m) => m.type === 'conditionalBlock');
                            const curRuleId = curCond?.attrs?.ruleId || JSON.stringify(curCond?.attrs?.rules || curCond?.attrs?.conditions || '');
                            if (curCond && curRuleId === condRuleId) {
                                group.push(curChild);
                                i++;
                            }
                            else {
                                break;
                            }
                        }
                        const condStr = buildConditionString(condMark.attrs);
                        const groupKey = globalIndex++;
                        result.push(<span key={groupKey} style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            ...parentStyle,
                            display: 'inline',
                        }} title={condStr ? `Condition: ${condStr}` : 'Conditional Block'}>
                            <span style={syntaxVioletStyle}>{'{'}</span>
                            {group.map((n) => renderInlineNode(n, parentStyle, isHeading))}
                            {condStr ? (<>{' '}<span style={syntaxVioletStyle}>{'['}</span>{renderConditionContent(condStr)}<span style={syntaxVioletStyle}>{']'}</span></>) : null}
                            <span style={syntaxVioletStyle}>{'}'}</span>
                        </span>);
                    }
                    else if (optMark) {
                        const group = [];
                        while (i < contentList.length) {
                            const curChild = contentList[i];
                            const curOpt = curChild.marks?.find((m) => m.type === 'optionalBlock');
                            if (curOpt) {
                                group.push(curChild);
                                i++;
                            }
                            else {
                                break;
                            }
                        }
                        const groupKey = globalIndex++;
                        result.push(<span key={groupKey} style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            ...parentStyle,
                            display: 'inline',
                        }} title="Optional Block">
                            <span style={syntaxVioletStyle}>{'{'}</span>
                            {group.map((n) => renderInlineNode(n, parentStyle, isHeading))}
                            <span style={syntaxVioletStyle}>{'}'}</span>
                        </span>);
                    }
                    else {
                        result.push(renderInlineNode(child, parentStyle, isHeading));
                        i++;
                    }
                }
                return result;
            }
            function processNode(node, parentStyle = {}) {
                const key = globalIndex++;
                const style = {
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    ...parentStyle,
                    textDecoration: 'none'
                };
                if (node.type === 'pageBreak') {
                    return (
                        <div key={key} style={{ margin: '4px 0', ...style }}>
                            <span style={{
                                backgroundColor: '#ede9fe',
                                color: '#6d28d9',
                                padding: '0 2px',
                                borderRadius: '2px',
                                display: 'inline',
                                fontWeight: 'normal',
                            }} title="Page Break">
                                &lt;page break&gt;
                            </span>
                        </div>
                    );
                }
                if (node.type === 'lineBreakBlock') {
                    return (<div key={key} style={{ margin: '2px 0', ...style }}>
                        <span style={{
                            backgroundColor: '#ede9fe',
                            color: '#6d28d9',
                            padding: '0 2px',
                            borderRadius: '2px',
                            display: 'inline',
                            fontWeight: 'normal',
                        }} title="Line Break">
                            &lt;line break&gt;
                        </span>
                    </div>);
                }
                if (node.type === 'horizontalLineBlock' || node.type === 'horizontalRule') {
                    return (
                        <div key={key} style={{ margin: '4px 0', width: '100%', ...style }}>
                            <span style={{
                                backgroundColor: '#ede9fe',
                                color: '#6d28d9',
                                padding: '0 2px',
                                borderRadius: '2px',
                                display: 'inline',
                                fontWeight: 'normal',
                            }} title="Line">
                                &lt;line&gt;
                            </span>
                        </div>
                    );
                }
                if (node.type === 'whiteBlankImage') {
                    return renderSignatureBox(node, key, style);
                }
                if (node.type === 'paragraph') {
                    let nodeText = '';
                    const extractText = (n) => {
                        if (n.text) nodeText += n.text;
                        if (n.type === 'variableTag' || n.attrs?.variableKey || n.attrs?.label) {
                            nodeText += ' ' + (n.attrs?.label || n.attrs?.variableKey || 'var');
                        }
                        if (n.content) n.content.forEach(extractText);
                    };
                    extractText(node);
                    const cleanText = nodeText.replace(/\u00a0/g, ' ').trim();
                    if (/^(?:e-?stamp\s+)?certificate\s+(?:no|number)[\s.:]*[A-Z0-9\-_/\s]+$/i.test(cleanText) || /^certificate\s+no\s*[:.]\s*in-[a-z0-9]+/i.test(cleanText)) {
                        return null;
                    }
                    const align = node.attrs?.textAlign;
                    let mb = '4px';
                    if (node.attrs?.marginBottom) {
                        const num = parseFloat(node.attrs.marginBottom);
                        if (!isNaN(num) && num > 0) {
                            mb = node.attrs.marginBottom;
                        }
                    }
                    let mt = '0';
                    if (node.attrs?.marginTop) {
                        const num = parseFloat(node.attrs.marginTop);
                        if (!isNaN(num) && num > 0) {
                            mt = node.attrs.marginTop;
                        }
                    }
                    const lh = node.attrs?.lineHeight;
                    const pStyle = {
                        textAlign: align || 'left',
                        marginTop: mt,
                        marginBottom: mb,
                        minHeight: '1.5em',
                        lineHeight: lh || 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        textIndent: node.attrs?.textIndent || undefined,
                        marginLeft: node.attrs?.marginLeft || undefined,
                        paddingLeft: node.attrs?.paddingLeft || undefined,
                        ...style,
                    };
                    const hasSpecialContent = Boolean(node.content?.some(c =>
                        c.type === 'image' ||
                        c.type === 'whiteBlankImage' ||
                        c.type === 'horizontalLineBlock' ||
                        c.type === 'horizontalRule' ||
                        c.type === 'pageBreak' ||
                        c.type === 'lineBreakBlock' ||
                        c.type === 'variableTag'
                    ));
                    if (cleanText === '##') {
                        return (
                            <div key={key} style={pStyle}>
                                <span style={{ color: '#6d28d9', backgroundColor: '#ede9fe', padding: '0 2px', borderRadius: '2px', fontWeight: 'bold' }}>
                                    ##
                                </span>
                            </div>
                        );
                    }
                    if (!cleanText && !hasSpecialContent) {
                        return <div key={key} style={pStyle}><br /></div>;
                    }
                    let prefix = '';
                    if (align === 'right')
                        prefix = '=> ';
                    else if (align === 'center')
                        prefix = '== ';
                    const prefixElem = prefix ? (
                        <span style={{ color: '#6d28d9', backgroundColor: '#ede9fe', padding: '0 2px', borderRadius: '2px', fontWeight: 'bold' }}>
                            {prefix}
                        </span>
                    ) : null;
                    const children = renderInlineGrouped(node.content || [], style);
                    const lastChild = node.content && node.content[node.content.length - 1];
                    const hasTrailingBreak = lastChild && (lastChild.type === 'hardBreak' || lastChild.type === 'br');
                    return (<div key={key} style={pStyle}>
                        {children.length > 0 ? (<>{prefixElem}{children}{hasTrailingBreak ? <br /> : null}</>) : <br />}
                    </div>);
                }
                if (node.type === 'heading') {
                    let hNodeText = '';
                    const extractHText = (n) => {
                        if (n.text) hNodeText += n.text;
                        if (n.attrs?.variableKey || n.attrs?.label) hNodeText += ' ' + (n.attrs.variableKey || n.attrs.label);
                        if (n.content) n.content.forEach(extractHText);
                    };
                    extractHText(node);
                    const cleanHText = hNodeText.replace(/\u00a0/g, ' ').trim();
                    const align = node.attrs?.textAlign || 'left';
                    const level = node.attrs?.level || 1;
                    let hMb = '4px';
                    if (node.attrs?.marginBottom) {
                        const num = parseFloat(node.attrs.marginBottom);
                        if (!isNaN(num) && num > 0) {
                            hMb = node.attrs.marginBottom;
                        }
                    }
                    let hMt = '8px';
                    if (node.attrs?.marginTop) {
                        const num = parseFloat(node.attrs.marginTop);
                        if (!isNaN(num) && num > 0) {
                            hMt = node.attrs.marginTop;
                        }
                    }
                    const hStyle = {
                        textAlign: align,
                        marginTop: hMt,
                        marginBottom: hMb,
                        fontWeight: 'bold',
                        minHeight: '1.5em',
                        lineHeight: node.attrs?.lineHeight || 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        ...style,
                    };
                    if (level === 2 && (!cleanHText || cleanHText === '##')) {
                        return (
                            <div key={key} style={hStyle}>
                                <span style={{ color: '#6d28d9', backgroundColor: '#ede9fe', padding: '0 2px', borderRadius: '2px', fontWeight: 'bold' }}>
                                    ##
                                </span>
                            </div>
                        );
                    }
                    if (!cleanHText) {
                        return <div key={key} style={hStyle}><br /></div>;
                    }
                    const headingVioletStyle = {
                        color: '#6d28d9',
                        backgroundColor: '#ede9fe',
                        padding: '0 2px',
                        borderRadius: '2px',
                    };
                    let prefix = '';
                    if (level === 1) {
                        hStyle.fontSize = '1em';
                        hStyle.textAlign = 'center';
                        hStyle.fontWeight = 'normal';
                        hStyle.textDecoration = 'none';
                        prefix = '# ';
                    }
                    if (level === 2) {
                        hStyle.fontSize = '1em';
                        hStyle.textAlign = 'center';
                        hStyle.fontWeight = 'bold';
                        hStyle.textDecoration = 'none';
                        prefix = '## ';
                    }
                    if (level === 3) {
                        hStyle.fontSize = '1em';
                        hStyle.textAlign = 'center';
                        hStyle.fontWeight = 'bold';
                        hStyle.textDecoration = 'underline';
                        prefix = '### ';
                    }
                    const prefixElem = <span style={{ ...headingVioletStyle, fontWeight: 'bold' }}>{prefix}</span>;
                    const innerStyle = { ...hStyle, textDecoration: 'none' };
                    let headingContent = node.content || [];
                    if (headingContent.length > 0 && headingContent[0].type === 'text') {
                        const firstText = headingContent[0].text || '';
                        if (/^(?:###|##|#)\s*/.test(firstText)) {
                            const newText = firstText.replace(/^(?:###|##|#)\s*/, '');
                            if (newText) {
                                headingContent = [
                                    {
                                        ...headingContent[0],
                                        text: newText
                                    },
                                    ...headingContent.slice(1)
                                ];
                            } else {
                                headingContent = headingContent.slice(1);
                            }
                        }
                    }
                    const children = renderInlineGrouped(headingContent, innerStyle, true);
                    if (level === 1)
                        return <h1 key={key} style={hStyle}>{prefixElem}{children.length > 0 ? children : <br />}</h1>;
                    if (level === 2)
                        return <h2 key={key} style={hStyle}>{prefixElem}{children.length > 0 ? children : <br />}</h2>;
                    if (level === 3)
                        return <h3 key={key} style={hStyle}>{prefixElem}{children.length > 0 ? children : <br />}</h3>;
                    return <div key={key} style={hStyle}>{prefixElem}{children.length > 0 ? children : <br />}</div>;
                }
                if (node.type === 'orderedList' || node.type === 'bulletList') {
                    const isOrdered = node.type === 'orderedList';
                    const listStyle = node.attrs?.listStyleType || (isOrdered ? 'decimal' : 'none');
                    const startNum = isOrdered ? (node.attrs?.start || 1) : undefined;
                    const children = (node.content || []).map((child) => {
                        if (child.type === 'listItem') {
                            return processNode(child, { ...parentStyle, isOrdered });
                        }
                        const subKey = globalIndex++;
                        return (
                            <li key={subKey} style={{ listStyleType: isOrdered ? listStyle : 'none', marginLeft: isOrdered ? '-24px' : '0', margin: '4px 0' }}>
                                {processNode(child, { ...parentStyle, isOrdered })}
                            </li>
                        );
                    });
                    const ListTag = isOrdered ? 'ol' : 'ul';
                    return (<ListTag key={key} {...(isOrdered && startNum ? { start: startNum } : {})} style={{
                        listStyleType: isOrdered ? listStyle : 'none',
                        paddingLeft: isOrdered ? '24px' : '0px',
                        marginTop: '4px',
                        marginBottom: '8px',
                        lineHeight: 1.5,
                    }}>
                        {children}
                    </ListTag>);
                }
                if (node.type === 'listItem') {
                    const itemContent = node.content || [];
                    const children = [];
                    const isOrdered = parentStyle?.isOrdered;
                    itemContent.forEach((block, bIdx) => {
                        if (block.type === 'paragraph') {
                            const pChildren = renderInlineGrouped(block.content || [], style);
                            if (bIdx === 0) {
                                children.push(...pChildren);
                            } else {
                                children.push(
                                    <div key={`p-${bIdx}`} style={{ marginTop: '2px', marginBottom: '2px' }}>
                                        {pChildren.length > 0 ? pChildren : <br />}
                                    </div>
                                );
                            }
                        } else if (block.type === 'pageBreak' || block.type === 'lineBreakBlock' || block.type === 'horizontalLineBlock' || block.type === 'horizontalRule') {
                            children.push(processNode(block, style));
                        } else {
                            const rendered = processNode(block, style);
                            if (rendered) children.push(rendered);
                        }
                    });
                    return (
                        <li key={key} style={{
                            listStyleType: isOrdered ? undefined : 'none',
                            marginBottom: '2px',
                            lineHeight: 1.5
                        }}>
                            {children.length > 0 ? children : <br />}
                        </li>
                    );
                }
                if (node.type === 'table') {
                    const tableFp = getNodeTableFingerprint(node);
                    // Do not drop tables from the editor document
                    const rows = (node.content || []).filter((r) => r.type === 'tableRow');
                    if (rows.length === 0)
                        return null;
                    const colCount = Math.max(...rows.map((r) => (r.content || []).length), 1);
                    const isAlignRow = (row) => {
                        const cells = row.content || [];
                        if (cells.length === 0) return false;
                        let hasAlignToken = false;
                        const allMatch = cells.every((cell) => {
                            let text = '';
                            const extractText = (n) => {
                                if (n.type === 'text')
                                    text += n.text;
                                if (n.content)
                                    n.content.forEach(extractText);
                            };
                            extractText(cell);
                            const trimmed = text.trim();
                            if (trimmed === '==' || trimmed === '<=' || trimmed === '=>' || trimmed === '=&gt;' || trimmed === '&lt;=' || trimmed === '<' || trimmed === '=' || trimmed === '>') {
                                hasAlignToken = true;
                                return true;
                            }
                            return false;
                        });
                        return hasAlignToken && allMatch;
                    };
                    const hasAlignRow = isAlignRow(rows[0]);
                    const elements = [];
                    const colAligns = [];
                    for (let i = 0; i < colCount; i++) {
                        let align = '<=';
                        for (const row of rows) {
                            const cell = (row.content || [])[i];
                            if (cell) {
                                const cellAlign = cell.attrs?.textAlign || cell.content?.[0]?.attrs?.textAlign;
                                if (cellAlign === 'right') {
                                    align = '=>';
                                    break;
                                }
                                if (cellAlign === 'left') {
                                    align = '<=';
                                    break;
                                }
                                if (cellAlign === 'center') {
                                    align = '==';
                                    break;
                                }
                            }
                        }
                        colAligns.push(align);
                    }
                    const hasCustomAlign = colAligns.some(a => a === '==' || a === '=>');
                    function renderAlignRow() {
                        const alignText = '|' + colAligns.join('|') + '|';
                        const alignKey = globalIndex++;
                        return (
                            <div key={alignKey} style={{ margin: '0', minHeight: '1.5em', lineHeight: 1.5, ...style }}>
                                <span style={{ color: '#6d28d9', backgroundColor: '#ede9fe', padding: '0 2px', borderRadius: '2px', fontWeight: 'bold' }}>
                                    {alignText}
                                </span>
                            </div>
                        );
                    }
                    function extractRowCondition(cells, row, tableNode) {
                        let rawCond = row?.attrs?.rowCondition || tableNode?.attrs?.tableCondition;
                        if (rawCond) {
                            const cleanKey = rawCond.replace(/^Condition:\s*/i, '').replace(/&lt;|&gt;|<|>|\[|\]|\{|\}/g, '').trim();
                            if (cleanKey) return cleanKey;
                        }
                        return null;
                    }

                    function renderCleanCellGrouped(contentList, cellStyle) {
                        const cleanList = (contentList || []).map(item => {
                            if (!item.marks) return item;
                            return {
                                ...item,
                                marks: item.marks.filter(m => m.type !== 'conditionalBlock' && m.type !== 'optionalBlock')
                            };
                        });
                        return renderInlineGrouped(cleanList, cellStyle);
                    }

                    function extractCellNodes(cell) {
                        if (!cell) return [];
                        const nodes = [];
                        (cell.content || []).forEach(block => {
                            if (block.content) {
                                block.content.forEach(n => {
                                    if (n.type !== 'hardBreak' && n.type !== 'br') {
                                        nodes.push(n);
                                    }
                                });
                            } else if (block.type !== 'paragraph' && block.type !== 'hardBreak' && block.type !== 'br') {
                                nodes.push(block);
                            }
                        });
                        return nodes;
                    }

                    function isNodeListEmpty(nodes) {
                        if (!nodes || nodes.length === 0) return true;
                        return nodes.every(n => {
                            if (!n) return true;
                            if (n.type === 'hardBreak' || n.type === 'br') return true;
                            if (n.type === 'text') {
                                return !n.text || !n.text.replace(/&nbsp;/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
                            }
                            return false;
                        });
                    }

                    if (!hasAlignRow) {
                        elements.push(renderAlignRow());
                    }

                    rows.forEach((row, rIdx) => {
                        if (isAlignRow(row)) return;
                        const rowKey = globalIndex++;
                        const cellElements = [];
                        const cells = row.content || [];
                        const isHeader = rIdx === 0 || cells.some(c => c?.type === 'tableHeader');
                        const rowCond = !isHeader ? extractRowCondition(cells, row, node) : null;

                        for (let i = 0; i < colCount; i++) {
                            const cell = cells[i];
                            const cellStyle = isHeader ? { fontWeight: 'bold', ...style } : { ...style };
                            const rawCellNodes = extractCellNodes(cell);
                            const isEmpty = isNodeListEmpty(rawCellNodes);
                            const cellContent = (cell && !isEmpty) ? ((rowCond || isHeader) ? renderCleanCellGrouped(rawCellNodes, cellStyle) : renderInlineGrouped(rawCellNodes, cellStyle)) : [];
                            cellElements.push(cellContent);
                        }

                        const syntaxVioletStyle = {
                            color: '#6d28d9',
                            backgroundColor: '#ede9fe',
                            padding: '0 2px',
                            borderRadius: '2px',
                            fontWeight: 'bold',
                        };

                        if (rowCond) {
                            const cleanCond = decodeHtmlEntities(rowCond).replace(/^Condition:\s*/i, '').replace(/&lt;|&gt;|<|>|\[|\]|\{|\}/g, '').trim();
                            let condVar = cleanCond;
                            let condOpVal = '';
                            if (cleanCond.includes('==')) {
                                const parts = cleanCond.split(/\s*==\s*/);
                                condVar = parts[0].trim();
                                condOpVal = ' == ' + parts[1].trim();
                            }
                            elements.push(<div key={rowKey} style={{ margin: '0', minHeight: '1.5em', lineHeight: 1.5, ...style }}>
                                <span style={syntaxVioletStyle}>{'{|'}</span>
                                {cellElements.map((c, idx) => (<React.Fragment key={idx}>
                                    {c && c.length > 0 ? <>{c}</> : ' '}
                                    <span style={syntaxVioletStyle}>{'|'}</span>
                                </React.Fragment>))}
                                <span style={syntaxVioletStyle}>{' ['}</span>
                                <span style={{ backgroundColor: '#ffff00', color: '#000000', padding: '0 2px', display: 'inline', fontWeight: 'bold' }}>{`<<${condVar}>>`}</span>
                                {condOpVal && <span style={syntaxVioletStyle}>{condOpVal}</span>}
                                <span style={syntaxVioletStyle}>{'] }'}</span>
                            </div>);
                        } else {
                            elements.push(<div key={rowKey} style={{ margin: '0', minHeight: '1.5em', lineHeight: 1.5, ...style }}>
                                <span style={syntaxVioletStyle}>{'|'}</span>
                                {cellElements.map((c, idx) => (<React.Fragment key={idx}>
                                    {c && c.length > 0 ? <>{c}</> : ' '}
                                    <span style={syntaxVioletStyle}>{'|'}</span>
                                </React.Fragment>))}
                            </div>);
                        }
                    });
                    return <div key={key} style={{ marginTop: '4px', marginBottom: '8px' }}>{elements}</div>;
                }
                if (node.content) {
                    return (node.content || []).map((child) => processNode(child));
                }
                return renderInlineNode(node, style);
            }
            function isBlankNode(node) {
                if (!node) return true;
                if (node.type === 'pageBreak' || node.type === 'lineBreakBlock' || node.type === 'horizontalLineBlock' || node.type === 'horizontalRule') {
                    return false;
                }
                if (node.type === 'paragraph') {
                    let text = '';
                    const extract = (n) => {
                        if (n.text) text += n.text;
                        if (n.type === 'variableTag' || n.attrs?.variableKey || n.attrs?.label) {
                            text += ' ' + (n.attrs?.label || n.attrs?.variableKey || '');
                        }
                        if (n.content) n.content.forEach(extract);
                    };
                    extract(node);
                    const clean = text.replace(/[\s\u00a0\u200B\r\n]+/g, '').trim();
                    const hasSpecial = Boolean(node.content?.some(c =>
                        c.type === 'image' ||
                        c.type === 'whiteBlankImage' ||
                        c.type === 'horizontalLineBlock' ||
                        c.type === 'horizontalRule' ||
                        c.type === 'pageBreak' ||
                        c.type === 'lineBreakBlock'
                    ));
                    return !clean && !hasSpecial;
                }
                return false;
            }

            const rawNodes = [...(editorJson.content || [])];
            while (rawNodes.length > 0 && isBlankNode(rawNodes[rawNodes.length - 1])) {
                rawNodes.pop();
            }

            const cleanedNodes = [];
            for (let i = 0; i < rawNodes.length; i++) {
                const cur = rawNodes[i];
                const isCurBlank = isBlankNode(cur);
                const nextNode = rawNodes[i + 1];
                const prevNode = cleanedNodes[cleanedNodes.length - 1];

                if (isCurBlank && ((nextNode && nextNode.type === 'pageBreak') || (prevNode && prevNode.type === 'pageBreak'))) {
                    continue;
                }
                if (isCurBlank && prevNode && isBlankNode(prevNode)) {
                    continue;
                }
                cleanedNodes.push(cur);
            }

            // Merge any stray {= [cond]} paragraph preceding a whiteBlankImage node
            for (let i = 0; i < cleanedNodes.length; i++) {
                const cur = cleanedNodes[i];
                if (cur && cur.type === 'paragraph') {
                    let text = '';
                    let condMark = null;
                    if (cur.content) {
                        for (const c of cur.content) {
                            if (c.text) text += c.text;
                            const cm = c.marks?.find(m => m.type === 'conditionalBlock');
                            if (cm && !condMark) condMark = cm;
                        }
                    }
                    const cleanT = text.replace(/[\s\u00a0\r\n]+/g, '').trim();
                    if (condMark && (cleanT === '=' || cleanT === '==' || cleanT === '=>' || cleanT === '')) {
                        let targetSig = null;
                        for (let j = i + 1; j < cleanedNodes.length; j++) {
                            const candidate = cleanedNodes[j];
                            if (candidate.type === 'whiteBlankImage') {
                                targetSig = candidate;
                                break;
                            } else if (candidate.type === 'paragraph' && candidate.content?.length === 1 && candidate.content[0].type === 'whiteBlankImage') {
                                targetSig = candidate.content[0];
                                break;
                            } else if (isBlankNode(candidate)) {
                                continue;
                            } else {
                                break;
                            }
                        }
                        if (targetSig) {
                            if (!targetSig.attrs) targetSig.attrs = {};
                            if (!targetSig.attrs.condition) {
                                const rawCond = buildConditionString(condMark.attrs) || condMark.attrs?.condition || '';
                                targetSig.attrs.condition = rawCond;
                                targetSig.attrs.conditionData = condMark.attrs?.rules || condMark.attrs?.conditions ? JSON.stringify(condMark.attrs) : '';
                            }
                            if (!targetSig.attrs.align || targetSig.attrs.align === 'left' || targetSig.attrs.align === 'center') {
                                targetSig.attrs.align = 'right';
                            }
                            cleanedNodes.splice(i, 1);
                            i--;
                        }
                    }
                }
            }

            // Process all nodes continuously without artificial page breaks causing large white voids
            const allPageElements = [];
            cleanedNodes.forEach((node) => {
                const processed = processNode(node);
                if (processed)
                    allPageElements.push(processed);
            });

            setPreviewElements([{
                pageNumber: 1,
                elements: allPageElements,
            }]);
            setSyntaxErrorCount(docErrorCount);
        };
        generatePreview();
        editor.on('update', generatePreview);
        return () => {
            editor.off('update', generatePreview);
        };
    }, [editor, variables, pageSettings]);
    const highlightDOMMatches = useCallback((container, query) => {
        if (!container) return [];

        const existingMarks = container.querySelectorAll('mark.doc-search-match');
        existingMarks.forEach((mark) => {
            const parent = mark.parentNode;
            if (parent) {
                while (mark.firstChild) {
                    parent.insertBefore(mark.firstChild, mark);
                }
                parent.removeChild(mark);
            }
        });
        container.normalize();

        const cleanQuery = (query || '').trim().toLowerCase();
        if (!cleanQuery) return [];

        const walker = document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode(node) {
                    if (!node.nodeValue || !node.nodeValue.toLowerCase().includes(cleanQuery)) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    if (node.parentElement && ['SCRIPT', 'STYLE'].includes(node.parentElement.tagName)) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    return NodeFilter.FILTER_ACCEPT;
                }
            }
        );

        const textNodes = [];
        let curr = walker.nextNode();
        while (curr) {
            textNodes.push(curr);
            curr = walker.nextNode();
        }

        const matchedMarks = [];
        textNodes.forEach((textNode) => {
            const parent = textNode.parentNode;
            if (!parent) return;

            const originalText = textNode.nodeValue;
            let remaining = originalText;
            const frag = document.createDocumentFragment();

            let idx = remaining.toLowerCase().indexOf(cleanQuery);
            while (idx !== -1) {
                if (idx > 0) {
                    frag.appendChild(document.createTextNode(remaining.substring(0, idx)));
                }
                const mark = document.createElement('mark');
                mark.className = 'doc-search-match';
                mark.textContent = remaining.substring(idx, idx + cleanQuery.length);
                mark.style.backgroundColor = '#e9d5ff';
                mark.style.color = '#000000';
                mark.style.borderRadius = '2px';
                mark.style.padding = '0 1px';
                frag.appendChild(mark);
                matchedMarks.push(mark);

                remaining = remaining.substring(idx + cleanQuery.length);
                idx = remaining.toLowerCase().indexOf(cleanQuery);
            }
            if (remaining.length > 0) {
                frag.appendChild(document.createTextNode(remaining));
            }
            parent.replaceChild(frag, textNode);
        });

        return matchedMarks;
    }, []);

    useEffect(() => {
        if (!previewDocRef.current) return;
        const foundMarks = highlightDOMMatches(previewDocRef.current, searchQuery);
        setMatches(foundMarks);
        if (foundMarks.length > 0) {
            setCurrentMatchIndex(0);
            foundMarks[0].style.backgroundColor = '#c084fc';
            foundMarks[0].style.color = '#000000';
            foundMarks[0].style.boxShadow = '0 0 0 2px #7c3aed';
            foundMarks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            setCurrentMatchIndex(0);
        }
    }, [searchQuery, previewElements, highlightDOMMatches]);

    const handleNextMatch = () => {
        if (matches.length === 0) return;
        const oldMark = matches[currentMatchIndex];
        if (oldMark) {
            oldMark.style.backgroundColor = '#e9d5ff';
            oldMark.style.color = '#000000';
            oldMark.style.boxShadow = 'none';
        }
        const nextIdx = (currentMatchIndex + 1) % matches.length;
        setCurrentMatchIndex(nextIdx);
        const newMark = matches[nextIdx];
        if (newMark) {
            newMark.style.backgroundColor = '#c084fc';
            newMark.style.color = '#000000';
            newMark.style.boxShadow = '0 0 0 2px #7c3aed';
            newMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handlePrevMatch = () => {
        if (matches.length === 0) return;
        const oldMark = matches[currentMatchIndex];
        if (oldMark) {
            oldMark.style.backgroundColor = '#e9d5ff';
            oldMark.style.color = '#000000';
            oldMark.style.boxShadow = 'none';
        }
        const prevIdx = (currentMatchIndex - 1 + matches.length) % matches.length;
        setCurrentMatchIndex(prevIdx);
        const newMark = matches[prevIdx];
        if (newMark) {
            newMark.style.backgroundColor = '#c084fc';
            newMark.style.color = '#000000';
            newMark.style.boxShadow = '0 0 0 2px #7c3aed';
            newMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) {
                handlePrevMatch();
            } else {
                handleNextMatch();
            }
        }
    };

    const applyPreviewZoom = useCallback((newZoomVal, explicitPoint = null) => {
        const clampedZoom = Math.max(50, Math.min(200, newZoomVal));
        if (clampedZoom === previewZoom) return;

        const container = previewScrollContainerRef.current;
        if (container) {
            let anchor = null;
            if (explicitPoint) {
                anchor = {
                    x: explicitPoint.x,
                    y: explicitPoint.y,
                    oldScrollTop: container.scrollTop,
                    oldScrollLeft: container.scrollLeft,
                    oldZoom: previewZoom,
                };
            } else if (previewMousePosRef.current?.isOver) {
                anchor = {
                    x: previewMousePosRef.current.x,
                    y: previewMousePosRef.current.y,
                    oldScrollTop: container.scrollTop,
                    oldScrollLeft: container.scrollLeft,
                    oldZoom: previewZoom,
                };
            } else {
                anchor = {
                    x: container.clientWidth / 2,
                    y: container.clientHeight / 2,
                    oldScrollTop: container.scrollTop,
                    oldScrollLeft: container.scrollLeft,
                    oldZoom: previewZoom,
                };
            }
            previewZoomAnchorRef.current = anchor;
        }
        setPreviewZoom(clampedZoom);
    }, [previewZoom]);

    const handleZoomIn = () => applyPreviewZoom(previewZoom + 10);
    const handleZoomOut = () => applyPreviewZoom(previewZoom - 10);
    const handleResetZoom = () => applyPreviewZoom(100);

    useEffect(() => {
        const anchor = previewZoomAnchorRef.current;
        if (!anchor) return;
        previewZoomAnchorRef.current = null;

        const container = previewScrollContainerRef.current;
        if (!container) return;

        const ratio = (previewZoom / 100) / (anchor.oldZoom / 100);
        const newScrollTop = (anchor.oldScrollTop + anchor.y) * ratio - anchor.y;
        const newScrollLeft = (anchor.oldScrollLeft + anchor.x) * ratio - anchor.x;

        container.scrollTop = Math.max(0, newScrollTop);
        container.scrollLeft = Math.max(0, newScrollLeft);
    }, [previewZoom]);

    useEffect(() => {
        const container = previewScrollContainerRef.current;
        if (!container) return;

        const handleWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const rect = container.getBoundingClientRect();
                const mousePoint = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                };
                if (e.deltaY < 0) {
                    applyPreviewZoom(previewZoom + 10, mousePoint);
                } else if (e.deltaY > 0) {
                    applyPreviewZoom(previewZoom - 10, mousePoint);
                }
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [previewZoom, applyPreviewZoom]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (searchQuery) {
                    setSearchQuery('');
                } else if (onClose) {
                    onClose();
                }
            } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                if (searchInputRef.current) searchInputRef.current.focus();
                if (searchInputRef.current) searchInputRef.current.select();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [searchQuery, onClose]);

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            <div style={{
                width: '100%', maxWidth: '1050px', height: '92vh', maxHeight: '940px',
                display: 'flex', flexDirection: 'column',
                backgroundColor: '#f0f2f5', borderRadius: '12px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                overflow: 'hidden'
            }}>
                {/* Teal Header matching Save and Export buttons with integrated Search */}
                <div style={{
                    padding: '12px 24px',
                    backgroundColor: 'var(--accent-teal, #068989)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    color: '#ffffff',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h2 style={{
                                margin: 0,
                                fontSize: '17px',
                                fontWeight: 700,
                                color: '#ffffff',
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap'
                            }}>
                                Document Preview
                            </h2>
                            <div style={{
                                margin: '2px 0 0 0',
                                fontSize: '11px',
                                color: 'rgba(255, 255, 255, 0.85)',
                                fontWeight: 400,
                                whiteSpace: 'nowrap'
                            }}>
                                A4 Page Size Layout
                            </div>
                        </div>

                        {/* Syntax Error / Validity Status Badge */}
                        {syntaxErrorCount > 0 ? (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.28)',
                                    border: '1px solid rgba(239, 68, 68, 0.7)',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                    animation: 'pulse 2s infinite'
                                }}
                                title={`${syntaxErrorCount} syntax error(s) detected in template. Look for red wavy underlines in the preview.`}
                            >
                                <AlertCircle size={15} style={{ color: '#fca5a5' }} />
                                <span>{syntaxErrorCount} Syntax {syntaxErrorCount === 1 ? 'Error' : 'Errors'}</span>
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                    border: '1px solid rgba(16, 185, 129, 0.5)',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}
                                title="All document syntax is valid."
                            >
                                <CheckCircle2 size={15} style={{ color: '#86efac' }} />
                                <span>Syntax Valid</span>
                            </div>
                        )}

                        {/* Search Document Bar */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.18)',
                            border: '1px solid rgba(255, 255, 255, 0.4)',
                            borderRadius: '8px',
                            padding: '5px 12px',
                            gap: '8px',
                            maxWidth: '380px',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}>
                            <Search size={16} color="#ffffff" style={{ flexShrink: 0 }} />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder="Search document"
                                className="search-doc-input"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    outline: 'none',
                                    color: '#ffffff',
                                    fontSize: '13px',
                                    width: '100%',
                                    fontFamily: 'inherit'
                                }}
                            />
                            {searchQuery && (
                                <span style={{
                                    fontSize: '11px',
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    padding: '0 4px',
                                    userSelect: 'none'
                                }}>
                                    {matches.length > 0 ? `${currentMatchIndex + 1}/${matches.length}` : '0/0'}
                                </span>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    onClick={handlePrevMatch}
                                    onMouseDown={(e) => e.preventDefault()}
                                    disabled={matches.length === 0}
                                    title="Previous match (Shift+Enter)"
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: matches.length > 0 ? 'pointer' : 'default',
                                        padding: '2px',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: matches.length > 0 ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                                        opacity: matches.length > 0 ? 1 : 0.5,
                                        transition: 'background-color 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => { if (matches.length > 0) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'; }}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNextMatch}
                                    onMouseDown={(e) => e.preventDefault()}
                                    disabled={matches.length === 0}
                                    title="Next match (Enter)"
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: matches.length > 0 ? 'pointer' : 'default',
                                        padding: '2px',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: matches.length > 0 ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                                        opacity: matches.length > 0 ? 1 : 0.5,
                                        transition: 'background-color 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => { if (matches.length > 0) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'; }}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    title="Clear search"
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '2px',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: '#ffffff',
                                    }}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <style>{`
                            .search-doc-input {
                                color: #ffffff !important;
                            }
                            .search-doc-input::placeholder {
                                color: rgba(255, 255, 255, 0.85) !important;
                            }
                        `}</style>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        title="Close preview"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            transition: 'background-color 0.15s ease, opacity 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <X size={22} />
                    </button>
                </div>

                {/* Subheader Toolbar with Zoom Controls on the Right Top */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '10px 24px 0 24px',
                    backgroundColor: '#f0f2f5',
                    borderBottom: 'none',
                    flexShrink: 0
                }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        padding: '3px 8px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}>
                        <button
                            type="button"
                            onClick={handleZoomOut}
                            disabled={previewZoom <= 50}
                            title="Zoom Out (-10%)"
                            style={{
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: previewZoom <= 50 ? 'not-allowed' : 'pointer',
                                opacity: previewZoom <= 50 ? 0.4 : 1,
                                padding: '5px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--accent-teal, #068989)',
                                transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => { if (previewZoom > 50) e.currentTarget.style.backgroundColor = 'rgba(6, 137, 137, 0.08)'; }}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                        >
                            <ZoomOut size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={handleResetZoom}
                            title="Reset Zoom to 100%"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#334155',
                                minWidth: '46px',
                                textAlign: 'center',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            {previewZoom}%
                        </button>
                        <button
                            type="button"
                            onClick={handleZoomIn}
                            disabled={previewZoom >= 200}
                            title="Zoom In (+10%)"
                            style={{
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: previewZoom >= 200 ? 'not-allowed' : 'pointer',
                                opacity: previewZoom >= 200 ? 0.4 : 1,
                                padding: '5px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--accent-teal, #068989)',
                                transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => { if (previewZoom < 200) e.currentTarget.style.backgroundColor = 'rgba(6, 137, 137, 0.08)'; }}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                        >
                            <ZoomIn size={16} />
                        </button>
                    </div>
                </div>

                <div
                    ref={previewScrollContainerRef}
                    onMouseMove={(e) => {
                        const container = e.currentTarget;
                        const rect = container.getBoundingClientRect();
                        previewMousePosRef.current = {
                            x: e.clientX - rect.left,
                            y: e.clientY - rect.top,
                            isOver: true,
                        };
                    }}
                    onMouseLeave={() => {
                        if (previewMousePosRef.current) {
                            previewMousePosRef.current.isOver = false;
                        }
                    }}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'auto',
                        padding: '28px 32px 60px 32px',
                        backgroundColor: '#525659',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start'
                    }}
                >
                    <div
                        ref={previewDocRef}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '32px',
                            zoom: previewZoom / 100,
                            transformOrigin: 'top center',
                            transition: 'zoom 0.15s ease',
                            paddingBottom: '40px',
                        }}
                    >
                        {Array.isArray(previewElements) && previewElements.map((page, idx) => (
                            <div
                                key={`preview-page-${idx}`}
                                className="preview-page-sheet"
                                style={{
                                    width: '210mm',
                                    maxWidth: '210mm',
                                    minHeight: '297mm',
                                    backgroundColor: '#ffffff',
                                    padding: `${pageSettings?.margins?.top || 20}mm ${pageSettings?.margins?.right || 20}mm ${pageSettings?.margins?.bottom || 20}mm ${pageSettings?.margins?.left || 20}mm`,
                                    boxShadow: '0 4px 24px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.18)',
                                    boxSizing: 'border-box',
                                    fontFamily: pageSettings?.fontFamily || 'Times New Roman',
                                    fontSize: `${pageSettings?.fontSize || 10}pt`,
                                    lineHeight: pageSettings?.lineSpacing || 1.15,
                                    color: '#000000',
                                    borderRadius: '2px',
                                    position: 'relative',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {page.elements && page.elements.length > 0 ? page.elements : (
                                    <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '12px', textAlign: 'center', marginTop: '120px' }}>
                                        (Blank Page)
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}